/** ステップ3: 申込内容の確認 (注文サマリ＋契約文書/個人情報の同意 / req2)。承認で注文作成＋approve */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { purchaseApi, type Product } from '../api/purchaseApi';
import { loadState, saveState } from '../state/purchaseState';
import { excludedOf, includedOf, annualExcludedOf, annualIncludedOf, firstPayBreakdown, contractBreakdown, yen } from '../state/calc';
import { sortByServiceOrder } from '../serviceOrder';
import { PurchaseLayout } from './PurchaseLayout';
// 正式な規約本文（プロジェクト提供の txt をそのまま取り込む）。
import CONTRACT_DOC from '../legal/terms.txt?raw';
import PRIVACY_DOC from '../legal/privacy.txt?raw';

/**
 * 規約テキストを整形表示する。1行目（タイトル）と「第X条…」の見出しを太字にする。
 * それ以外の行はそのまま表示（空行は行間として反映）。
 */
const ARTICLE_RE = /^第[0-9０-９]+条/;
const LegalDoc: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.replace(/\r\n/g, '\n').replace(/\n+$/, '').split('\n');
  return (
    <>
      {lines.map((line, i) => {
        if (line.trim() === '') return <div key={i} style={{ height: '0.7em' }} aria-hidden="true" />;
        const isTitle = i === 0;
        const bold = isTitle || ARTICLE_RE.test(line);
        return (
          <div
            key={i}
            style={{
              fontWeight: bold ? 700 : 400,
              fontSize: isTitle ? '1.05em' : undefined,
              margin: bold ? '8px 0 2px' : 0,
            }}
          >
            {line}
          </div>
        );
      })}
    </>
  );
};

export const PurchaseConfirmPage: React.FC = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const st = loadState();
  const [products, setProducts] = useState<Product[]>([]);
  // 契約文書 / 個人情報: 最後までスクロールしたら了解チェックを有効化
  const [contractReadEnd, setContractReadEnd] = useState(false);
  const [privacyReadEnd, setPrivacyReadEnd] = useState(false);
  const [contractAgreed, setContractAgreed] = useState(false); // 初期OFF・必須
  const [privacyAgreed, setPrivacyAgreed] = useState(false); // 初期OFF・必須
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!st.billingCompanyId) { nav('/purchase/account'); return; }
    // 表示順を GBなび→GB人財→GB組織 に統一（申込 items の登録順もこれに従う）。
    purchaseApi.getProducts().then((r) => setProducts(sortByServiceOrder(r.products, (p) => p.code)));
  }, []);

  const lines = useMemo(
    () => products.filter((p) => st.cart[p.code]?.checked).map((p) => ({ p, l: st.cart[p.code] })),
    [products]
  );

  // 金額は税抜表示。1回目支払い＝実際の初回請求(GBなび月払いは初月分)／契約内容＝年間総額(GBなびは年額換算)。
  const firstExcl = lines.reduce((s, x) => s + excludedOf(x.p, x.l.userCount, x.l.paymentMethod), 0);
  const firstIncl = lines.reduce((s, x) => s + includedOf(x.p, x.l.userCount, x.l.paymentMethod), 0);
  const contractExcl = lines.reduce((s, x) => s + annualExcludedOf(x.p, x.l.userCount), 0);
  const contractIncl = lines.reduce((s, x) => s + annualIncludedOf(x.p, x.l.userCount), 0);

  /** スクロールが末尾に到達したら markRead() を呼ぶ */
  const onScrollDoc = (markRead: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) markRead();
  };

  // 決済に進める条件: 契約文書・個人情報の両方に同意 (データ分析はゲートしない)
  const canProceed = contractAgreed && privacyAgreed;

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const items = lines.map((x) => ({ productCode: x.p.code, userCount: x.l.userCount, paymentMethod: x.l.paymentMethod }));
      const { order } = await purchaseApi.createOrder({
        billingCompanyId: st.billingCompanyId!,
        items,
        consent: { contractAgreed, privacyAgreed, dataAnalysisOptIn: true },
      });
      await purchaseApi.approveOrder(order.id);
      // 支払い方法選択は廃止（銀行振込のみ・申込受付まで）。承認後そのまま受付確定し完了へ。
      await purchaseApi.checkout(order.id, 'bank_transfer');
      saveState({ ...st, orderId: order.id });
      nav('/purchase/complete');
    } catch (e) {
      setErr(e instanceof Error ? e.message : '確定に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PurchaseLayout step={3} sectionTitle="お申し込み内容の確認" sectionSub="内容と契約条件をご確認のうえ、ご同意ください">
      <div className="pf-form">
        {sp.get('dup') && <div className="pf-warn">同じ会社/決済者の情報が既に登録されている可能性があります（重複検知）。手続きは続行できます。</div>}
        {err && <div className="pf-err">{err}</div>}

        <h3 style={{ margin: '4px 0 8px', fontSize: 15 }}>1回目支払い内容</h3>
        {lines.map((x) => (
          <div className="pf-sum-row" key={x.p.code}>
            <span>{x.p.name}（{firstPayBreakdown(x.p, x.l.paymentMethod)}）× {x.l.userCount}名</span>
            <span>{yen(excludedOf(x.p, x.l.userCount, x.l.paymentMethod))}</span>
          </div>
        ))}
        <div className="pf-sum-row"><span>小計</span><span>{yen(firstExcl)}</span></div>
        <div className="pf-sum-row"><span>消費税</span><span>{yen(firstIncl - firstExcl)}</span></div>
        <div className="pf-sum-row" style={{ fontWeight: 700 }}><span>合計</span><span>{yen(firstIncl)}</span></div>

        <h3 style={{ margin: '18px 0 8px', fontSize: 15 }}>契約内容</h3>
        {lines.map((x) => (
          <div className="pf-sum-row" key={x.p.code}>
            <span>{x.p.name}（{contractBreakdown(x.p)}）× {x.l.userCount}名</span>
            <span>{yen(annualExcludedOf(x.p, x.l.userCount))}</span>
          </div>
        ))}
        <div className="pf-sum-row"><span>小計</span><span>{yen(contractExcl)}</span></div>
        <div className="pf-sum-row"><span>消費税</span><span>{yen(contractIncl - contractExcl)}</span></div>
        <div className="pf-sum-row" style={{ fontWeight: 700 }}><span>合計</span><span>{yen(contractIncl)}</span></div>

        <div className="pf-terms">
          <h3>契約期間</h3>
          <p>契約期間はマイページのアカウント情報、または利用規約をご参照ください。</p>
          <h3>お支払い方法</h3>
          <p>お支払いは、請求書による銀行振込となります。請求書は別途メールにてお送りいたします。</p>
          <h3>利用者登録</h3>
          <p>利用者登録については、お申し込み完了後に別途ご案内いたします。</p>
        </div>

        {/* 利用規約 (必須・スクロール末尾で了解可) */}
        <div className="pf-doc-block">
          <h3>利用規約</h3>
          <div className="pf-doc" onScroll={onScrollDoc(() => setContractReadEnd(true))}>
            <LegalDoc text={CONTRACT_DOC} />
          </div>
          <label className={`pf-consent${contractReadEnd ? '' : ' is-disabled'}`}>
            <input
              type="checkbox"
              disabled={!contractReadEnd}
              checked={contractAgreed}
              onChange={(e) => setContractAgreed(e.target.checked)}
            />
            了解しました（利用規約）
            {!contractReadEnd && <span className="pf-consent-hint">※ 最後までお読みください</span>}
          </label>
        </div>

        {/* 個人情報保護方針 (必須・スクロール末尾で了解可) */}
        <div className="pf-doc-block">
          <h3>個人情報保護方針</h3>
          <div className="pf-doc" onScroll={onScrollDoc(() => setPrivacyReadEnd(true))}>
            <LegalDoc text={PRIVACY_DOC} />
          </div>
          <label className={`pf-consent${privacyReadEnd ? '' : ' is-disabled'}`}>
            <input
              type="checkbox"
              disabled={!privacyReadEnd}
              checked={privacyAgreed}
              onChange={(e) => setPrivacyAgreed(e.target.checked)}
            />
            了解しました（個人情報保護方針）
            {!privacyReadEnd && <span className="pf-consent-hint">※ 最後までお読みください</span>}
          </label>
        </div>

        <div className="pf-actions">
          <button className="back" onClick={() => nav('/purchase/account')}>戻る</button>
          <button className="go" disabled={!canProceed || busy} onClick={submit}>{busy ? '処理中…' : '同意して申し込む'}</button>
        </div>
      </div>
    </PurchaseLayout>
  );
};

export default PurchaseConfirmPage;
